import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './GuildWarCount.css';
import { useI18n } from '../i18n';
import { getContentPage } from '../content/publicContent';
import ContentSections from './ContentSections';

const GuildWar = () => {
  const { locale, messages, t } = useI18n();
  const copy = messages.guildWarCount;
  const pageContent = getContentPage(locale, 'guildWarCount');
  const [gid, setGid] = useState('');
  const [defenders, setDefenders] = useState([]);
  const [guildNames, setGuildNames] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [missingTodayCount, setMissingTodayCount] = useState(0);
  const [nationalFlags, setNationalFlags] = useState([]); // nationalFlagのリスト
  const [selectedFlag, setSelectedFlag] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' }); // ソート設定
  const [teamType, setTeamType] = useState('ally'); // 味方・敵の選択

  // 日本時間での現在の日時を取得
  const getJapanDate = () => {
    const japanTimeOffset = 9 * 60; // 日本標準時 (UTC+9) のオフセット（分）
    const localDate = new Date();
    const utcDate = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000); // UTC時間を取得
    return new Date(utcDate.getTime() + japanTimeOffset * 60000); // 日本標準時に変換
  };

  // GuildWarコンポーネント内で使用する関数の修正
  const getDatesOfThisWeek = () => {
  const currentDate = getJapanDate();
  //console.log('currentDate:', currentDate);
  let firstDayOfWeek;
  // 現在の時間が0~8時の場合
  if (currentDate.getHours() >= 0 && currentDate.getHours() < 9) {
    if (currentDate.getDay() === 0) { // 日曜日の場合
      firstDayOfWeek = currentDate.getDate() - currentDate.getDay() - 6;
    } else {
      firstDayOfWeek = currentDate.getDate() - currentDate.getDay() + 1;
    }
  } else {
    if (currentDate.getDay() === 0) { // 日曜日の場合
      firstDayOfWeek = currentDate.getDate() - currentDate.getDay() - 7;
    } else {
      firstDayOfWeek = currentDate.getDate() - currentDate.getDay();
    }
  }

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const daysToDates = {};

  weekDays.forEach((day, index) => {
    const targetDate = new Date(currentDate);
    // Sunday is the last day of the Mon-Sun week, so treat it as index 7 not 0
    const adjustedIndex = day === 'Sunday' ? 7 : index;
    targetDate.setDate(firstDayOfWeek + adjustedIndex);
    daysToDates[day] = targetDate.toISOString().split('T')[0];
  });

  return daysToDates;
};

const datesOfThisWeek = getDatesOfThisWeek();
const getCurrentDay = () => {
  const currentDate = getJapanDate();
  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  //console.log('currentDate.getDay:', currentDate.getDay());
  return weekDays[currentDate.getDay()];
};

const currentDay = getCurrentDay();

  const fetchGuildWarData = useCallback(async () => {
    setIsLoading(true);
    try {
      setIsError(false);

      const response = await axios.get(
        `https://rangers-api-3mfw.onrender.com/api/guild-member-info/${gid}/members`,
      );

      const data = response.data.members.map(defender => ({
        ...defender,
        pvpLeaguePhase: translatePvpLeaguePhase(defender.pvpLeaguePhase),
      }));

      const sortedData = data.sort((a, b) => sortPvpLeaguePhase(a.pvpLeaguePhase, b.pvpLeaguePhase));

      setDefenders(sortedData);
    } catch (error) {
      setIsError(true);
      console.error('データの取得中にエラーが発生しました:', error);
      console.error('エラーの詳細:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        gid: gid,
        url: `https://rangers-api-3mfw.onrender.com/api/guild-member-info/${gid}/members`
      });
    } finally {
      setIsLoading(false);
    }
  }, [gid]);

  const fetchGuildNames = async () => {
    try {
      const response = await axios.get('https://rangers-api-3mfw.onrender.com/api/guilds/get-guild-names');
      const guilds = response.data.filter(guild => guild.lastGuildWarRank >= 1 && guild.lastGuildWarRank <= 500);
      const guildNamesWithFlags = guilds.map(guild => ({ gid: guild.gid, name: guild.name, nationalFlag: guild.nationalFlag }));

      const uniqueFlags = new Set(guildNamesWithFlags.map(guild => guild.nationalFlag));
      setNationalFlags([...uniqueFlags]); // 重複のないnationalFlagのリストを設定

      setGuildNames([
        { gid: '00e6947f32afa26dc1dfcc19f1a2628e', name: 'ドリチー', nationalFlag: 'JP' }, // 固定のギルド名を追加
        ...guildNamesWithFlags,
      ]);
    } catch (error) {
      console.error('ギルド名の取得中にエラーが発生しました:', error);
    }
  };

  const fetchCheckboxStates = useCallback(async () => {
    if (!gid) return;
    try {
      const response = await axios.get(`https://rangers-api-3mfw.onrender.com/api/guilds/get-checkbox-states`, {
        params: {
          gid,
          type: teamType,
        },
      });
      const states = response.data;
      setDefenders(prevDefenders =>
        prevDefenders.map(defender => ({
          ...defender,
          checkboxStates: states[defender.uid] || {},
        }))
      );
    } catch (error) {
      console.error('チェックボックス状態の取得中にエラーが発生しました:', error);
    }
  }, [gid, teamType]);

  // ギルドデータを取得し、その後チェックボックス状態を取得
  useEffect(() => {
    if (gid) {
      fetchGuildWarData().then(() => {
        // ギルドデータ取得後にチェックボックス状態を取得
        fetchCheckboxStates();
      });
    }
  }, [gid, fetchGuildWarData, fetchCheckboxStates]);

  // teamType変更時はチェックボックス状態のみ更新
  useEffect(() => {
    if (gid && defenders.length > 0) {
      fetchCheckboxStates();
    }
  }, [teamType, fetchCheckboxStates, gid, defenders.length]);

  useEffect(() => {
    fetchGuildNames();
  }, []);

  useEffect(() => {
    // `missing-today` クラスのカウントを更新
    let count = 0;
    defenders.forEach(defender => {
      const checkboxStates = defender.checkboxStates || {};
      if (currentDay === 'Wednesday' && (!checkboxStates[datesOfThisWeek.Wednesday]?.[1] || !checkboxStates[datesOfThisWeek.Wednesday]?.[2])) {
        if (!checkboxStates[datesOfThisWeek.Wednesday]?.[1]) count++;
        if (!checkboxStates[datesOfThisWeek.Wednesday]?.[2]) count++;
      }
      if (currentDay === 'Friday' && (!checkboxStates[datesOfThisWeek.Friday]?.[1] || !checkboxStates[datesOfThisWeek.Friday]?.[2])) {
        if (!checkboxStates[datesOfThisWeek.Friday]?.[1]) count++;
        if (!checkboxStates[datesOfThisWeek.Friday]?.[2]) count++;
      }
      if (currentDay === 'Sunday' && (!checkboxStates[datesOfThisWeek.Sunday]?.[1] || !checkboxStates[datesOfThisWeek.Sunday]?.[2])) {
        if (!checkboxStates[datesOfThisWeek.Sunday]?.[1]) count++;
        if (!checkboxStates[datesOfThisWeek.Sunday]?.[2]) count++;
      }
    });
    setMissingTodayCount(count);
  }, [defenders, currentDay, datesOfThisWeek]);

  const handleCheckboxChange = async (uid, day, slot) => {
    try {
      const defender = defenders.find(d => d.uid === uid);
      const checkboxStates = defender.checkboxStates || {};
      const isSlot1Checked = checkboxStates[day]?.[1] || false;
      const isSlot2Checked = checkboxStates[day]?.[2] || false;
      await axios.post('https://rangers-api-3mfw.onrender.com/api/guilds/save-checkbox', {
        gid,
        uid,
        day,
        slot,
        type: teamType
      });

      // 2のチェックがついた場合、1も自動的にチェックする
      if (!isSlot1Checked && !isSlot2Checked && slot === 2) {
        await axios.post('https://rangers-api-3mfw.onrender.com/api/guilds/save-checkbox', {
          gid,
          uid,
          day,
          slot: 1,
          type: teamType,
        });
      }

      fetchCheckboxStates(); // 保存後にチェックボックスの状態を更新
    } catch (error) {
      console.error('チェックボックス状態の保存中にエラーが発生しました:', error);
    }
  };

  const selectGroup = (
    <div className="select-group">
      <div className="checker-header">
        <h1>{copy.title}</h1>
        <p>{copy.note}</p>
      </div>
      <div className="checker-controls" aria-label={copy.title}>
        <label className="checker-field">
          <span className="checker-label">{copy.country}</span>
          <select
            className="checker-select"
            value={selectedFlag}
            onChange={(e) => setSelectedFlag(e.target.value)}
          >
            <option value="">{copy.all}</option>
            {nationalFlags.map((flag, index) => (
              <option key={index} value={flag}>{flag}</option>
            ))}
          </select>
        </label>
        <label className="checker-field checker-field-wide">
          <span className="checker-label">{copy.guild}</span>
          <select
            className="checker-select"
            onChange={(e) => {
              setGid(e.target.value);
            }}
            value={gid}
          >
            <option value="">{copy.select}</option>
            {guildNames
              .filter(guild => !selectedFlag || guild.nationalFlag === selectedFlag) // フラグでフィルタリング
              .map((guild, index) => (
                <option key={index} value={guild.gid}>{guild.name}</option>
              ))}
          </select>
        </label>
        <fieldset className="checker-field team-field">
          <legend className="checker-label">{copy.team}</legend>
          <div className="team-toggle" role="radiogroup" aria-label={copy.team}>
            <label className={teamType === 'ally' ? 'active' : ''}>
              <input
                type="radio"
                name="teamType"
                value="ally"
                checked={teamType === 'ally'}
                onChange={(e) => setTeamType(e.target.value)}
              />
              <span>{copy.ally}</span>
            </label>
            <label className={teamType === 'enemy' ? 'active' : ''}>
              <input
                type="radio"
                name="teamType"
                value="enemy"
                checked={teamType === 'enemy'}
                onChange={(e) => setTeamType(e.target.value)}
              />
              <span>{copy.enemy}</span>
            </label>
          </div>
        </fieldset>
      </div>
    </div>
  );

  // レベルを表示形式に変換する関数
  const formatLevel = (level) => {
    if (level < 100) {
      return `Lv${level}`;
    } else if (level < 199) {
      return `M${level - 99}`;
    } else if (level < 298) {
      return `S${level - 198}`;
    } else if (level < 397) {
      return `U${level - 297}`;
    } else if (level < 496) {
      return `L${level - 396}`;
    } else {
      return `S'${level - 495}`;
    }
  };

  // ソート関数
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // ソート済みのdefendersを取得する関数
  const getSortedDefenders = () => {
    if (!sortConfig.key) {
      return defenders;
    }

    return [...defenders].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'level':
          aValue = a.level || 0;
          bValue = b.level || 0;
          break;
        case 'name':
          aValue = (a.userName || '').toLowerCase();
          bValue = (b.userName || '').toLowerCase();
          break;
        case 'pvp':
          // PVPソートは既存のsortPvpLeaguePhase関数を使用
          const translatePhase = (phase) => translatePvpLeaguePhase(phase);
          aValue = translatePhase(a.pvpLeaguePhase || '');
          bValue = translatePhase(b.pvpLeaguePhase || '');
          return sortConfig.direction === 'asc' 
            ? sortPvpLeaguePhase(aValue, bValue)
            : sortPvpLeaguePhase(bValue, aValue);
        default:
          return 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const translatePvpLeaguePhase = (phase) => {
    switch (phase) {
      case 'LEGEND':
        return 'L';
      case 'MASTER_1':
        return 'M1';
      case 'MASTER_2':
        return 'M2';
      case 'MASTER_3':
        return 'M3';
      case 'DIAMOND_1':
        return 'D1';
      case 'DIAMOND_2':
        return 'D2';
      case 'DIAMOND_3':
        return 'D3';
      case 'GOLD_1':
        return 'G1';
      case 'GOLD_2':
        return 'G2';
      case 'GOLD_3':
        return 'G3';
      case 'SILVER_1':
        return 'S1';
      case 'SILVER_2':
        return 'S2';
      case 'SILVER_3':
        return 'S3';
      case 'BRONZE':
        return 'B';
      default:
        return phase;
    }
  };

  const sortPvpLeaguePhase = (phaseA, phaseB) => {
    const order = [
      'L', 'M1', 'M2', 'M3', 'D1', 'D2', 'D3', 'G1', 'G2', 'G3', 'S1', 'S2', 'S3', 'B'
    ];
    return order.indexOf(phaseA) - order.indexOf(phaseB);
  };

  return (
    <div className="guild-war-container">
      <ContentSections
        sections={pageContent.sections}
        className="tool-context"
        collapsible
        summaryLabel={pageContent.summaryLabel}
      />
      {selectGroup} {/* 選択部分は常に表示 */}

      {isError ? (
        <p>{copy.error}</p>
      ) : isLoading ? (
        <p>{copy.loading}</p>
      ) : (
        <>
          <p>{t('guildWarCount.remainingToday', { count: missingTodayCount })}</p>
          <br />
          <table className="defender-table">
            <thead>
              <tr>
                <th rowSpan="2" style={{cursor: 'pointer'}} onClick={() => handleSort('level')}>
                  {copy.level} {sortConfig.key === 'level' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th rowSpan="2" style={{cursor: 'pointer'}} onClick={() => handleSort('name')}>
                  {copy.name} {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th rowSpan="2" style={{cursor: 'pointer'}} onClick={() => handleSort('pvp')}>
                  {copy.pvp} {sortConfig.key === 'pvp' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th colSpan="2" className={currentDay === 'Wednesday' ? 'today' : ''}>Wed.</th>
                <th colSpan="2" className={currentDay === 'Friday' ? 'today' : ''}>Fri.</th>
                <th colSpan="2" className={currentDay === 'Sunday' ? 'today' : ''}>Sun.</th>
              </tr>
              <tr>
                <th className={currentDay === 'Wednesday' ? 'today' : ''}>1</th>
                <th className={currentDay === 'Wednesday' ? 'today' : ''}>2</th>
                <th className={currentDay === 'Friday' ? 'today' : ''}>1</th>
                <th className={currentDay === 'Friday' ? 'today' : ''}>2</th>
                <th className={currentDay === 'Sunday' ? 'today' : ''}>1</th>
                <th className={currentDay === 'Sunday' ? 'today' : ''}>2</th>
              </tr>
            </thead>
            <tbody>
              {getSortedDefenders().map((defender, index) => {
                const playerName = defender.userName || '名前不明';
                const playerLevel = defender.level || 1;
                const levelDisplay = formatLevel(playerLevel);
                const pvpLeaguePhase = defender.pvpLeaguePhase || 'PVP不明';
                const checkboxStates = defender.checkboxStates || {};

                return (
                  <tr key={index} className="defender-row">
                    <td>{levelDisplay}</td>
                    <td>{playerName}</td>
                    <td>{pvpLeaguePhase}</td>
                    <td className={currentDay === 'Wednesday' && !checkboxStates[datesOfThisWeek.Wednesday]?.[1] ? 'missing-today' : ''}>
                      <input
                        type="checkbox"
                        checked={checkboxStates[datesOfThisWeek.Wednesday]?.[1] || false}
                        onChange={() => handleCheckboxChange(defender.uid, datesOfThisWeek.Wednesday, 1)}
                      />
                    </td>
                    <td className={currentDay === 'Wednesday' && !checkboxStates[datesOfThisWeek.Wednesday]?.[2] ? 'missing-today' : ''}>
                      <input
                        type="checkbox"
                        checked={checkboxStates[datesOfThisWeek.Wednesday]?.[2] || false}
                        onChange={() => handleCheckboxChange(defender.uid, datesOfThisWeek.Wednesday, 2)}
                      />
                    </td>
                    <td className={currentDay === 'Friday' && !checkboxStates[datesOfThisWeek.Friday]?.[1] ? 'missing-today' : ''}>
                      <input
                        type="checkbox"
                        checked={checkboxStates[datesOfThisWeek.Friday]?.[1] || false}
                        onChange={() => handleCheckboxChange(defender.uid, datesOfThisWeek.Friday, 1)}
                      />
                    </td>
                    <td className={currentDay === 'Friday' && !checkboxStates[datesOfThisWeek.Friday]?.[2] ? 'missing-today' : ''}>
                      <input
                        type="checkbox"
                        checked={checkboxStates[datesOfThisWeek.Friday]?.[2] || false}
                        onChange={() => handleCheckboxChange(defender.uid, datesOfThisWeek.Friday, 2)}
                      />
                    </td>
                    <td className={currentDay === 'Sunday' && !checkboxStates[datesOfThisWeek.Sunday]?.[1] ? 'missing-today' : ''}>
                      <input
                        type="checkbox"
                        checked={checkboxStates[datesOfThisWeek.Sunday]?.[1] || false}
                        onChange={() => handleCheckboxChange(defender.uid, datesOfThisWeek.Sunday, 1)}
                      />
                    </td>
                    <td className={currentDay === 'Sunday' && !checkboxStates[datesOfThisWeek.Sunday]?.[2] ? 'missing-today' : ''}>
                      <input
                        type="checkbox"
                        checked={checkboxStates[datesOfThisWeek.Sunday]?.[2] || false}
                        onChange={() => handleCheckboxChange(defender.uid, datesOfThisWeek.Sunday, 2)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default GuildWar;
